import { useEffect } from "react";

/**
 * Globally enables the browser's native autofill / suggestion dropdown on every
 * `<input>` and `<textarea>` in the app.
 *
 * Async-select (react-select) fields are intentionally left untouched: their inner
 * `<input>` exposes its own typeahead via `role="combobox"` / `aria-autocomplete`,
 * so we must not interfere with it.
 */

// Input types where the browser suggestion dropdown is irrelevant.
const SKIPPED_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "hidden",
  "file",
  "submit",
  "button",
  "image",
  "reset",
  "range",
  "color",
]);

// react-select renders its search input as an ARIA combobox; these markers
// identify async-select fields without colliding with Bootstrap's "form-control".
const isAsyncSelectInput = (el) =>
  el.getAttribute("role") === "combobox" || el.hasAttribute("aria-autocomplete");

const shouldEnable = (el) => {
  if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return false;
  if (isAsyncSelectInput(el)) return false;
  if (el.tagName === "INPUT") {
    const type = (el.getAttribute("type") || "text").toLowerCase();
    if (SKIPPED_INPUT_TYPES.has(type)) return false;
  }
  return true;
};

const applyTo = (el) => {
  if (!shouldEnable(el)) return;
  el.setAttribute("autocomplete", "on");
};

const applyToTree = (root) => {
  if (!root) return;
  if (root.nodeType === Node.ELEMENT_NODE) applyTo(root);
  if (typeof root.querySelectorAll === "function") {
    root.querySelectorAll("input, textarea").forEach(applyTo);
  }
};

export const useEnableBrowserAutocomplete = () => {
  useEffect(() => {
    applyToTree(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) applyToTree(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);
};

export default useEnableBrowserAutocomplete;
